import { PopoverAnchor } from '@radix-ui/react-popover';
import type { ComponentPropsWithoutRef } from 'react';
import React from 'react';
import { useBreakpoint } from '../../lib/hooks/use-breakpoints.ts';
import { Drawer } from './drawer.tsx';
import { Popover, PopoverContent, PopoverTrigger } from './popover.tsx';

type BreakpointKey = 'sm' | 'md' | 'lg' | 'xl' | '2xl';

const useIsDesktop = (breakpoint: BreakpointKey = 'sm') => {
    const breakpointResult = useBreakpoint(breakpoint);
    return breakpointResult[
        `is${breakpoint.charAt(0).toUpperCase()}${breakpoint.slice(1)}` as keyof typeof breakpointResult
    ];
};

/**
 * Root component that wraps children in Popover on desktop.
 */
type RootProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    children: React.ReactNode;
};

const ResponsivePopover = ({ open, onOpenChange, children }: RootProps) => {
    const isDesktop = useIsDesktop();

    if (!isDesktop) {
        return <>{children}</>;
    }

    return (
        <Popover open={open} onOpenChange={onOpenChange}>
            {children}
        </Popover>
    );
};

/**
 * Trigger element that opens the popover/drawer.
 */
type TriggerProps = {
    children: React.ReactNode;
    asChild?: boolean;
    onOpenChange: (open: boolean) => void;
};

const Trigger = ({ children, asChild, onOpenChange }: TriggerProps) => {
    const isDesktop = useIsDesktop();

    if (!isDesktop) {
        if (asChild && React.isValidElement(children)) {
            return React.cloneElement(children as React.ReactElement<{ onClick?: () => void }>, {
                onClick: () => {
                    onOpenChange(true);
                },
            });
        }
        return (
            <div
                onClick={() => {
                    onOpenChange(true);
                }}
            >
                {children}
            </div>
        );
    }

    return <PopoverTrigger asChild={asChild}>{children}</PopoverTrigger>;
};

/**
 * Anchor element for positioning the popover (desktop only).
 */
type AnchorProps = {
    children: React.ReactNode;
    asChild?: boolean;
};

const Anchor = ({ children, asChild }: AnchorProps) => {
    const isDesktop = useIsDesktop();

    if (!isDesktop) {
        return <>{children}</>;
    }

    return <PopoverAnchor asChild={asChild}>{children}</PopoverAnchor>;
};

/**
 * Content of the popover/drawer.
 */
type ContentProps = Omit<ComponentPropsWithoutRef<typeof PopoverContent>, 'children'> & {
    children: React.ReactNode;
    open: boolean;
    onOpenChange: (open: boolean) => void;
};

const Content = ({ children, open, onOpenChange, ...popoverContentProps }: ContentProps) => {
    const isDesktop = useIsDesktop();

    if (!isDesktop) {
        return (
            <Drawer
                open={open}
                onClose={() => {
                    onOpenChange(false);
                }}
            >
                {children}
            </Drawer>
        );
    }

    return <PopoverContent {...popoverContentProps}>{children}</PopoverContent>;
};

ResponsivePopover.Trigger = Trigger;
ResponsivePopover.Anchor = Anchor;
ResponsivePopover.Content = Content;

export { ResponsivePopover };
